import React, { useRef } from "react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Download, Award, Info } from "lucide-react";
import {
  RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Radar,
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, Cell,
} from "recharts";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";
import { toast } from "sonner";
import { REPORT } from "@/constants/testIds";

const sigColor = {
  High: "bg-blue-600 text-white",
  Moderate: "bg-blue-100 text-blue-700 border border-blue-200",
  Low: "bg-slate-100 text-slate-600 border border-slate-200",
  "Very Low": "bg-slate-50 text-slate-400 border border-slate-200",
};

export default function Report({ report, onRestart }) {
  const ref = useRef(null);
  const isMsis = report.testType === "MSIS";

  const downloadPdf = async () => {
    if (!ref.current) return;
    toast.info("Preparing your PDF report…");
    try {
      const canvas = await html2canvas(ref.current, { scale: 2, backgroundColor: "#ffffff", useCORS: true });
      const img = canvas.toDataURL("image/png");
      const pdf = new jsPDF("p", "mm", "a4");
      const pw = pdf.internal.pageSize.getWidth();
      const ph = pdf.internal.pageSize.getHeight();
      const iw = pw;
      const ih = (canvas.height * pw) / canvas.width;
      let left = ih;
      let pos = 0;
      pdf.addImage(img, "PNG", 0, pos, iw, ih);
      left -= ph;
      while (left > 0) {
        pos = left - ih;
        pdf.addPage();
        pdf.addImage(img, "PNG", 0, pos, iw, ih);
        left -= ph;
      }
      pdf.save(`${report.testType}_Report_${report.testId}.pdf`);
    } catch (e) {
      toast.error("Could not generate PDF. Please try again.");
    }
  };

  const radarData = report.domains.map((d) => ({ domain: d.code, score: d.percentage }));
  const barData = [...report.domains].map((d) => ({ name: d.code, value: d.percentage }));

  return (
    <div className="max-w-6xl mx-auto px-5 py-10">
      <div className="flex flex-wrap items-center justify-between gap-4 mb-8">
        <div>
          <p className="eyebrow">{report.testType} Assessment Report</p>
          <h1 className="text-3xl sm:text-4xl font-bold tracking-tight">{isMsis ? "Your Specialization Inclination" : "Your Specialization Orientation"}</h1>
        </div>
        <div className="flex gap-3">
          <Button data-testid={REPORT.downloadPdf} onClick={downloadPdf} className="rounded-full bg-blue-700 hover:bg-blue-800 text-white">
            <Download className="w-4 h-4 mr-2" /> Download PDF
          </Button>
          <Button variant="outline" onClick={onRestart} className="rounded-full">Done</Button>
        </div>
      </div>

      <div ref={ref} data-testid={REPORT.container} className="bg-white p-8 rounded-lg border border-slate-200">
        <div className="flex items-center justify-between border-b border-slate-200 pb-5 mb-6">
          <div>
            <p className="eyebrow">MSI Scale</p>
            <h2 className="text-2xl font-bold">{isMsis ? "Management Specialization Inclination Scale" : "Management Specialization Orientation Scale"}</h2>
          </div>
          <div className="text-right text-sm text-slate-500">
            <div>Test ID: <span className="font-semibold text-slate-800">{report.testId}</span></div>
            <div>{report.institution}</div>
            <div>{new Date(report.completedAt).toLocaleDateString()}</div>
          </div>
        </div>

        {/* Recommendation + overall */}
        <div className="grid md:grid-cols-3 gap-5 mb-8">
          <div data-testid={REPORT.recommended} className="md:col-span-2 rounded-lg bg-slate-900 text-white p-7">
            <div className="flex items-center gap-2 text-blue-300"><Award className="w-5 h-5" /><span className="eyebrow text-blue-300">{isMsis ? "Top Inclination" : "Recommended Specialization"}</span></div>
            <div className="text-3xl sm:text-4xl font-bold mt-3">{report.recommended?.name}</div>
            <p className="text-slate-300 mt-2 text-sm">{isMsis ? `Your preferences and attitudes point most strongly toward this domain (${report.recommended?.percentage}% inclination).` : `Your general-intelligence reasoning cues point most strongly toward this domain (${report.recommended?.percentage}% of domain items keyed correct).`}</p>
          </div>
          <div data-testid={REPORT.overall} className="rounded-lg border border-slate-200 p-7 flex flex-col justify-center">
            <span className="eyebrow">{isMsis ? "Overall Inclination" : "Overall Score"}</span>
            <div className="text-4xl font-bold mt-2">{report.overall.score}<span className="text-xl text-slate-400">/{report.overall.max}</span></div>
            <div className="text-blue-700 font-semibold mt-1">{report.overall.percentage}%{isMsis ? "" : " correct"}</div>
          </div>
        </div>

        {/* Charts */}
        <div className="grid md:grid-cols-2 gap-6 mb-8">
          <div className="border border-slate-200 rounded-lg p-4">
            <p className="eyebrow mb-3">Domain Profile</p>
            <ResponsiveContainer width="100%" height={260}>
              {report.domains.length >= 3 ? (
                <RadarChart data={radarData}>
                  <PolarGrid stroke="#E2E8F0" />
                  <PolarAngleAxis dataKey="domain" tick={{ fill: "#475569", fontSize: 12 }} />
                  <PolarRadiusAxis domain={[0, 100]} tick={{ fill: "#94A3B8", fontSize: 10 }} />
                  <Radar dataKey="score" stroke="#1D4ED8" fill="#1D4ED8" fillOpacity={0.35} />
                </RadarChart>
              ) : (
                <BarChart data={barData}>
                  <XAxis dataKey="name" tick={{ fill: "#475569", fontSize: 12 }} />
                  <YAxis domain={[0, 100]} tick={{ fill: "#94A3B8", fontSize: 10 }} />
                  <Tooltip />
                  <Bar dataKey="value" radius={[4, 4, 0, 0]}>
                    {barData.map((_, i) => <Cell key={i} fill="#1D4ED8" />)}
                  </Bar>
                </BarChart>
              )}
            </ResponsiveContainer>
          </div>
          <div className="border border-slate-200 rounded-lg p-4">
            <p className="eyebrow mb-3">Domain Scores (%)</p>
            <ResponsiveContainer width="100%" height={260}>
              <BarChart data={barData} layout="vertical" margin={{ left: 10 }}>
                <XAxis type="number" domain={[0, 100]} tick={{ fill: "#94A3B8", fontSize: 10 }} />
                <YAxis type="category" dataKey="name" tick={{ fill: "#475569", fontSize: 12 }} width={44} />
                <Tooltip />
                <Bar dataKey="value" radius={[0, 4, 4, 0]}>
                  {barData.map((_, i) => <Cell key={i} fill={i === 0 ? "#0F172A" : "#1D4ED8"} />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Domain + construct detail */}
        <p className="eyebrow mb-3">Detailed Construct Analysis</p>
        <div className="space-y-6">
          {report.domains.map((d, i) => (
            <div key={d.code} data-testid={REPORT.domainRow} className="border border-slate-200 rounded-lg overflow-hidden">
              <div className="flex items-center justify-between px-5 py-3 bg-slate-50 border-b border-slate-200">
                <div className="flex items-center gap-3">
                  {i === 0 && <Badge className="bg-blue-700 text-white">Top domain</Badge>}
                  <h3 className="text-lg font-semibold">{d.name}</h3>
                </div>
                <div className="text-sm font-semibold text-slate-700">{d.score}/{d.max} &middot; {d.percentage}%</div>
              </div>
              <div className="divide-y divide-slate-100">
                {d.constructs.map((c) => (
                  <div key={c.code} className="px-5 py-3 grid grid-cols-12 gap-3 items-center">
                    <div className="col-span-12 sm:col-span-5">
                      <div className="font-medium text-slate-800">{c.name}</div>
                      <div className="text-xs text-slate-500 leading-snug">{c.probes}</div>
                    </div>
                    <div className="col-span-6 sm:col-span-4">
                      <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                        <div className="h-full bg-blue-600" style={{ width: `${(c.score / c.max) * 100}%` }} />
                      </div>
                    </div>
                    <div className="col-span-3 sm:col-span-1 text-sm font-semibold text-slate-700">{c.score}/{c.max}</div>
                    <div className="col-span-3 sm:col-span-2 text-right">
                      <span className={`text-xs px-2 py-1 rounded-full font-semibold ${sigColor[c.significance] || sigColor.Low}`}>{c.significance}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>

        <div className="mt-8 flex gap-2 text-xs text-slate-500 border-t border-slate-200 pt-4">
          <Info className="w-4 h-4 shrink-0 mt-0.5" />
          <p>{isMsis ? "MSIS is a psychometric inclination scale. Domain scores reflect your self-reported preferences, attitudes and personality across management areas and should be interpreted as guidance, not a definitive aptitude verdict. \"Significance\" reflects how strongly you leaned toward each construct." : "MSOS is a development/pilot orientation scale. Raw domain scores are provisional indicators of reasoning orientation and should be interpreted as guidance, not a definitive aptitude verdict. \"Significance\" reflects how strongly each construct was answered correctly within the domain."}</p>
        </div>
      </div>
    </div>
  );
}
